// Rejestr ośrodka w raporcie (etap 2): ta sama tabela co w rejestrze ośrodka, zmiany zapisywane z odnośnikiem do raportu.
// Raport zaakceptowany pokazuje migawkę z chwili akceptacji, tylko do odczytu.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createIssue, deleteIssue, downloadIssuesTemplate, downloadResortIssues, fetchReportIssues, parseIssuesSpreadsheet,
  updateIssue, type SiteIssue,
} from "../../api/siteIssuesApi";
import { getTemplateProblems } from "../../api/questionsApi";
import { ReportState } from "../../types/Rows";
import { IssueLogGrid, IssueLogSource } from "../report/actionLog/IssueLogGrid";
import { SITE_ISSUE_RULES } from "../report/actionLog/actionLogFormat";
import { rowFromIssue, stateFromRow } from "./siteIssueRows";
import { SiteIssueHistoryModal } from "./SiteIssueHistoryModal";

const HEADERS = { potentialSeriousFinding: "Poważne", deviationDescription: "Opis" };

const day = (iso: string) => new Date(iso.endsWith("Z") ? iso : iso + "Z").toLocaleDateString("pl-PL");

export const ReportSiteIssuesSection = ({ reportId, reportState }: { reportId: string; reportState: ReportState }) => {
  const queryClient = useQueryClient();
  const queryKey = ["siteIssues", "report", reportId];
  const { data, isLoading, error } = useQuery(queryKey, () => fetchReportIssues(reportId));
  const { data: dictionary } = useQuery(["problemsDictionary"], getTemplateProblems, { staleTime: 10 * 60 * 1000 });
  const categoryName = (id: number) => dictionary?.categories?.find((c: { id: number; value: string }) => c.id === id)?.value ?? String(id);
  const [historyOf, setHistoryOf] = useState<SiteIssue | null>(null);

  const readOnly = reportState === ReportState.ACCEPTED || !!data?.snapshot;
  const byId = useMemo(() => new Map((data?.items ?? []).map((i) => [i.id, i])), [data]);
  const rows = useMemo(() => data?.items.map(rowFromIssue), [data]);
  const review = data?.items.filter((i) => i.needsReview).length ?? 0;

  const refresh = () => {
    queryClient.invalidateQueries(queryKey);
    if (data) queryClient.invalidateQueries(["siteIssues", data.resortId]);
  };

  const source: IssueLogSource = {
    rows,
    isLoading,
    error,
    save: async (row) => {
      const state = stateFromRow(row, row.id ? byId.get(row.id) : undefined);
      if (row.id) {
        await updateIssue(row.id, state, reportId);
        return row.id;
      }
      return createIssue(data!.resortId, state, reportId);
    },
    remove: (id) => deleteIssue(id, reportId),
    refresh,
    parseXlsx: parseIssuesSpreadsheet,
    downloadTemplate: downloadIssuesTemplate,
    downloadData: () => downloadResortIssues(data!.resortId),
  };

  const description = readOnly ? (
    <>
      Raport zaakceptowany: stan rejestru
      {data?.snapshot ? ` z dnia akceptacji (${day(data.snapshot.takenAt)})` : ""}, tylko do odczytu.
      Zmiany wprowadzisz w rejestrze ośrodka.
    </>
  ) : (
    <>
      Wszystkie znaleziska i akcje ośrodka. Zmiany zapisują się w rejestrze ośrodka z odnośnikiem do tego raportu.
      {review > 0 && <> Do weryfikacji: <strong>{review}</strong>.</>}
    </>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#C07810]">
          Rejestr ośrodka (wersja testowa)
        </span>
        {data && (
          <Link to={`/resorts/${data.resortId}/issues`} className="text-[12px] text-[var(--sc-blue)] hover:underline">
            Otwórz rejestr ośrodka →
          </Link>
        )}
      </div>
      <IssueLogGrid
        source={source}
        title={data ? `Znaleziska i akcje: ${data.resortName}` : "Znaleziska i akcje ośrodka"}
        description={description}
        rules={SITE_ISSUE_RULES}
        headers={HEADERS}
        readOnly={readOnly}
        dataExport={{ label: "Rejestr ośrodka (.xlsx)", hint: "wszystkie pozycje do pracy w Excelu", fileName: "Rejestr_osrodka" }}
        renderRowActions={(row) => (
          <button
            type="button"
            className="text-[11px] text-[var(--sc-blue)] hover:underline px-1"
            onClick={() => setHistoryOf(byId.get(row.id!) ?? null)}
          >
            historia
          </button>
        )}
      />
      {historyOf && (
        <SiteIssueHistoryModal
          issueId={historyOf.id}
          title={historyOf.description.slice(0, 60)}
          categoryName={categoryName}
          onClose={() => setHistoryOf(null)}
        />
      )}
    </div>
  );
};
